/**
 * Template Library Component
 *
 * Displays available HVAC templates with category filtering and search.
 */

import { useMemo } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  useTemplateStore,
  useFilteredTemplates,
} from './templateStore';
import { TEMPLATE_CATEGORY_INFO, type TemplateCategory } from './types';

/**
 * Category icon component
 */
function CategoryIcon({ category }: { category: TemplateCategory }) {
  const icons: Record<TemplateCategory, string> = {
    vav: 'M12 3v18m-6-6h12M6 9h12',
    packaged: 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4',
    vrf: 'M13 10V3L4 14h7v7l9-11h-7z',
    radiant: 'M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z',
    residential: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6',
    advanced: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z',
  };

  return (
    <svg
      className="w-5 h-5"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d={icons[category]} />
    </svg>
  );
}

/**
 * Template card component
 */
function TemplateCard({
  id,
  name,
  description,
  category,
  applicability,
  onSelect,
}: {
  id: string;
  name: string;
  description: string;
  category: TemplateCategory;
  applicability: { sizeRange: { min: number; max: number } };
  onSelect: (id: string) => void;
}) {
  const categoryInfo = TEMPLATE_CATEGORY_INFO[category];

  return (
    <div
      className="group border rounded-lg p-4 hover:border-primary hover:shadow-md transition-all cursor-pointer bg-card"
      onClick={() => onSelect(id)}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-md bg-secondary text-secondary-foreground">
            <CategoryIcon category={category} />
          </div>
          <div>
            <h3 className="font-semibold text-sm group-hover:text-primary transition-colors">
              {name}
            </h3>
            <span className="text-xs text-muted-foreground">{categoryInfo.name}</span>
          </div>
        </div>
      </div>

      {/* Description */}
      <p className="text-xs text-muted-foreground mb-3 line-clamp-2">{description}</p>

      {/* Footer */}
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>
          {applicability.sizeRange.min.toLocaleString()} -{' '}
          {applicability.sizeRange.max.toLocaleString()} m²
        </span>
        <Button variant="ghost" size="sm" className="h-7 px-2 opacity-0 group-hover:opacity-100 transition-opacity">
          Select
        </Button>
      </div>
    </div>
  );
}

/**
 * Category filter tabs
 */
function CategoryTabs() {
  const selectedCategory = useTemplateStore((s) => s.selectedCategory);
  const setSelectedCategory = useTemplateStore((s) => s.setSelectedCategory);
  const templates = useTemplateStore((s) => s.templates);

  const categoryCounts = useMemo(() => {
    const counts = new Map<TemplateCategory | 'all', number>();
    counts.set('all', templates.length);
    for (const template of templates) {
      counts.set(template.category, (counts.get(template.category) || 0) + 1);
    }
    return counts;
  }, [templates]);

  const categories: Array<{ key: TemplateCategory | null; label: string }> = [
    { key: null, label: 'All' },
    { key: 'vav', label: 'VAV' },
    { key: 'packaged', label: 'Packaged' },
    { key: 'vrf', label: 'VRF' },
    { key: 'radiant', label: 'Radiant' },
    { key: 'residential', label: 'Residential' },
    { key: 'advanced', label: 'Advanced' },
  ];

  return (
    <div className="flex gap-1 flex-wrap">
      {categories.map(({ key, label }) => {
        const count = categoryCounts.get(key ?? 'all') || 0;
        const isSelected = selectedCategory === key;

        return (
          <button
            key={key ?? 'all'}
            onClick={() => setSelectedCategory(key)}
            className={cn(
              'px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
              isSelected
                ? 'bg-primary text-primary-foreground'
                : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
            )}
          >
            {label}
            <span className="ml-1 opacity-70">({count})</span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * Search input component
 */
function SearchInput() {
  const searchQuery = useTemplateStore((s) => s.searchQuery);
  const setSearchQuery = useTemplateStore((s) => s.setSearchQuery);

  return (
    <div className="relative">
      <svg
        className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
        />
      </svg>
      <input
        type="text"
        placeholder="Search templates..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        className="w-full pl-9 pr-4 py-2 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
      />
    </div>
  );
}

/**
 * Template Library component
 */
export function TemplateLibrary() {
  const filteredTemplates = useFilteredTemplates();
  const startWizard = useTemplateStore((s) => s.startWizard);

  const handleSelectTemplate = (templateId: string) => {
    startWizard(templateId);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b space-y-4">
        <div>
          <h2 className="text-lg font-semibold">HVAC Templates</h2>
          <p className="text-sm text-muted-foreground">
            Select a template to quickly create an HVAC system
          </p>
        </div>
        <SearchInput />
        <CategoryTabs />
      </div>

      {/* Template Grid */}
      <div className="flex-1 overflow-auto p-4">
        {filteredTemplates.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground">
            <svg
              className="w-12 h-12 mb-4 opacity-50"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p className="text-sm">No templates found</p>
            <p className="text-xs">Try adjusting your search or filters</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredTemplates.map((template) => (
              <TemplateCard
                key={template.id}
                id={template.id}
                name={template.name}
                description={template.description}
                category={template.category}
                applicability={template.applicability}
                onSelect={handleSelectTemplate}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default TemplateLibrary;
